import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Portal,
  Tabs,
  Tab,
  Stack,
  Divider,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const SECTION_CONFIGS = [
  {
    key: "formUploadData1",
    title: "Purchase Contract Data",
    description:
      "The following fields are optional, but when provided they power offer comparison, deal timelines and other data-driven features.",
  },
  {
    key: "formUploadData2",
    title: "Additional Dates - Part 1 of 2",
    description:
      "The following fields are optional, but when provided they power deal timelines and other data-driven features.",
  },
  {
    key: "formUploadData3",
    title: "Additional Dates - Part 2 of 2",
    description:
      "The following fields are less common, but when provided they power deal timelines and other data-driven features.",
  },
];

const FormUploadStructure = ({ fields, value = [], onChange }) => {
  const [sections, setSections] = useState(value);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize active section if needed
  useEffect(() => {
    if (sections.length > 0 && !activeSectionId) {
      setActiveSectionId(sections[0].id);
    } else if (sections.length === 0 && activeSectionId) {
      setActiveSectionId(null);
    }
  }, [sections, activeSectionId]);

  // Compute available fields (formUpload: true, not assigned to any section)
  const includedFields = fields.filter((f) => f.formUpload);
  const assignedFieldIds = sections.flatMap((s) => s.fieldIds);

  // Filter unassigned fields based on search
  const unassignedFields = includedFields.filter(
    (f) => !assignedFieldIds.includes(f.id)
  ).filter(f =>
    !searchTerm ||
    (f.label && f.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (f.attributeName && f.attributeName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Remove fields from sections if they are no longer included
  useEffect(() => {
    const includedIds = includedFields.map((f) => f.id);
    let changed = false;
    const newSections = sections.map((section) => {
      const filtered = section.fieldIds.filter((fid) =>
        includedIds.includes(fid)
      );
      if (filtered.length !== section.fieldIds.length) changed = true;
      return { ...section, fieldIds: filtered };
    });
    if (changed) {
      setSections(newSections);
      onChange && onChange(newSections);
    }
  }, [includedFields, sections, onChange]);

  // Add section (max 3)
  const handleAddSection = () => {
    if (sections.length >= 3) return;
    const config = SECTION_CONFIGS[sections.length];
    setSections((prev) => {
      const newSection = {
        id: config.key,
        title: config.title,
        description: config.description,
        fieldIds: [],
      };
      const next = [...prev, newSection];
      onChange && onChange(next);
      return next;
    });
    // Switch to the new section automatically
    // Need to use config.key as that's what we set the ID to
    setTimeout(() => setActiveSectionId(config.key), 0);
  };

  // Remove section
  const handleRemoveSection = (id) => {
    setSections((prev) => {
      const next = prev.filter((s) => s.id !== id);
      onChange && onChange(next);
      return next;
    });
    if (activeSectionId === id) {
      setActiveSectionId(null); // Effect will pick the next one
    }
  };

  // Edit section
  const handleEditSection = (section) => {
    setEditingSection(section);
    setEditForm({
      title: section.title,
      description: section.description,
    });
  };

  // Save edit
  const handleSaveEdit = () => {
    if (editingSection) {
      setSections((prev) => {
        const next = prev.map((s) =>
          s.id === editingSection.id
            ? { ...s, title: editForm.title, description: editForm.description }
            : s
        );
        onChange && onChange(next);
        return next;
      });
      setEditingSection(null);
      setEditForm({ title: "", description: "" });
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditForm({ title: "", description: "" });
  };

  // Add drag state management for global cursor
  useEffect(() => {
    return () => {
      document.body.classList.remove("grabbing-cursor");
    };
  }, []);

  const handleDragStart = () => {
    document.body.classList.add("grabbing-cursor");
  };

  const onDragEnd = (result) => {
    document.body.classList.remove("grabbing-cursor");
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    // Dropping in same list at same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    let newSections = [...sections];

    // CASE 1: Reordering within active section (most common)
    if (source.droppableId === destination.droppableId && source.droppableId !== "uploadUnassigned") {
      const sectionIdx = newSections.findIndex(s => s.id === source.droppableId);
      if (sectionIdx !== -1) {
        const fieldIds = Array.from(newSections[sectionIdx].fieldIds);
        fieldIds.splice(source.index, 1);
        fieldIds.splice(destination.index, 0, draggableId);
        newSections[sectionIdx] = { ...newSections[sectionIdx], fieldIds };
      }
    }
    // CASE 2: Dragging from available (unassigned) to active section
    else if (source.droppableId === "uploadUnassigned" && destination.droppableId === activeSectionId) {
      const sectionIdx = newSections.findIndex(s => s.id === destination.droppableId);
      if (sectionIdx !== -1) {
        const fieldIds = Array.from(newSections[sectionIdx].fieldIds);
        fieldIds.splice(destination.index, 0, draggableId);
        newSections[sectionIdx] = { ...newSections[sectionIdx], fieldIds };
      }
    }
    // Note: We don't support dragging back to "unassigned" via drag-and-drop area 
    // because it's cleaner to just use the trash icon or delete button.
    // Also cross-section dragging isn't needed in this view since only one section is visible.

    setSections(newSections);
    onChange && onChange(newSections);
  };

  // Helper to add field directly to active section
  const handleAddFieldToActive = (fieldId) => {
    if (!activeSectionId) return;

    setSections(prev => {
      const newSections = [...prev];
      const sectionIdx = newSections.findIndex(s => s.id === activeSectionId);
      if (sectionIdx !== -1) {
        // Add to end of list
        const fieldIds = [...newSections[sectionIdx].fieldIds, fieldId];
        newSections[sectionIdx] = { ...newSections[sectionIdx], fieldIds };
      }
      onChange && onChange(newSections);
      return newSections;
    });
  };

  // Helper to remove field from a section
  const handleRemoveFieldFromSection = (fieldId, sectionId) => {
    setSections(prev => {
      const newSections = [...prev];
      const sectionIdx = newSections.findIndex(s => s.id === sectionId);
      if (sectionIdx !== -1) {
        const fieldIds = newSections[sectionIdx].fieldIds.filter(id => id !== fieldId);
        newSections[sectionIdx] = { ...newSections[sectionIdx], fieldIds };
      }
      onChange && onChange(newSections);
      return newSections;
    });
  };

  const renderFieldContent = (field, isDragging = false, isAssigned = false, sectionId = null) => (
    <Box
      sx={{ display: "flex", alignItems: "flex-start", width: "100%", py: 0.5 }}
    >
      {isAssigned && (
        <DragIndicatorIcon sx={{ mr: 1.5, mt: 0.5, opacity: 0.4 }} />
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            color: isDragging ? "common.white" : "text.primary",
            lineHeight: 1.3,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
            minWidth: 0,
            flexGrow: 1,
          }}
        >
          {field.label || field.attributeName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Chip
            label={field.type}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              bgcolor: isDragging ? "rgba(255, 255, 255, 0.2)" : "action.hover",
              color: isDragging ? "common.white" : "text.secondary",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              color: isDragging ? "common.white" : "text.secondary",
              fontSize: "0.7rem",
              flexGrow: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {field.attributeName || "No attribute name"}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      {!isAssigned && activeSectionId && (
        <Tooltip title="Add to active section">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleAddFieldToActive(field.id)}
            sx={{ ml: 1 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {isAssigned && (
        <Tooltip title="Remove from section API">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleRemoveFieldFromSection(field.id, sectionId)}
            sx={{ ml: 1, opacity: 0.6, '&:hover': { opacity: 1 } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const activeSection = sections.find(s => s.id === activeSectionId);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title="Form Upload Structure"
        subheader="Organize fields into logical sections."
        action={
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleAddSection}
            disabled={sections.length >= 3}
          >
            Add Section ({sections.length}/3)
          </Button>
        }
        sx={{ pb: 1 }}
      />

      {/* Section Tabs */}
      {sections.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeSectionId}
            onChange={(_, newVal) => setActiveSectionId(newVal)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map(section => (
              <Tab
                key={section.id}
                label={section.title}
                value={section.id}
              />
            ))}
          </Tabs>
        </Box>
      )}

      <DragDropContext onDragStart={handleDragStart} onDragEnd={onDragEnd}>
        <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>

          {/* Left Panel: Available Fields */}
          <Grid item xs={12} md={5} sx={{ borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '600px' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                Available Fields ({unassignedFields.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Droppable droppableId="uploadUnassigned" isDropDisabled={true}>
              {(provided, snapshot) => (
                <List
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    p: 1,
                    bgcolor: 'background.default'
                  }}
                >
                  {unassignedFields.map((field, index) => (
                    <Draggable
                      key={field.id}
                      draggableId={field.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <>
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              bgcolor: snapshot.isDragging ? "primary.main" : "background.paper",
                              mb: 1,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              "&:hover": {
                                bgcolor: "action.hover",
                                boxShadow: 1
                              }
                            }}
                          >
                            {renderFieldContent(field, snapshot.isDragging)}
                          </ListItem>
                          {snapshot.isDragging && (
                            <ListItem sx={{ display: 'none' }}>
                              {/* Placeholder to prevent layout shift if needed */}
                            </ListItem>
                          )}
                        </>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {unassignedFields.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center', opacity: 0.6 }}>
                      <Typography variant="body2">
                        {searchTerm ? 'No matching fields found' : 'All upload fields assigned'}
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Droppable>
          </Grid>

          {/* Right Panel: Active Section */}
          <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', height: '600px', bgcolor: 'grey.50' }}>
            {activeSection ? (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6">{activeSection.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activeSection.description}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" onClick={() => handleEditSection(activeSection)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveSection(activeSection.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>

                <Droppable droppableId={activeSection.id}>
                  {(provided, snapshot) => (
                    <List
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: 2,
                        transition: 'background-color 0.2s',
                        bgcolor: snapshot.isDraggingOver ? 'rgba(0, 168, 150, 0.05)' : 'transparent'
                      }}
                    >
                      {activeSection.fieldIds.length === 0 && !snapshot.isDraggingOver && (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          opacity: 0.5,
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 2,
                          m: 2
                        }}>
                          <ArrowForwardIcon sx={{ fontSize: 40, mb: 1 }} />
                          <Typography>Drag fields here or click + to add</Typography>
                        </Box>
                      )}

                      {activeSection.fieldIds.map((fid, idx) => {
                        const field = includedFields.find(f => f.id === fid);
                        if (!field) return null;
                        return (
                          <Draggable
                            key={fid}
                            draggableId={fid}
                            index={idx}
                          >
                            {(provided, snapshot) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  bgcolor: snapshot.isDragging ? "primary.main" : "background.paper",
                                  mb: 1,
                                  borderRadius: 1,
                                  boxShadow: 1,
                                  border: "1px solid",
                                  borderColor: "transparent",
                                  "&:hover": {
                                    borderColor: "primary.main"
                                  }
                                }}
                              >
                                {renderFieldContent(field, snapshot.isDragging, true, activeSection.id)}
                              </ListItem>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Selection
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Select a section from the tabs above or create a new one to start adding fields.
                </Typography>
                {sections.length === 0 && (
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={handleAddSection}
                  >
                    Create First Section
                  </Button>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </DragDropContext>

      {/* Edit Section Dialog */}
      <Dialog
        open={!!editingSection}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Upload Section</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Section Title"
              value={editForm.title}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Section Description"
              value={editForm.description}
              onChange={(e) =>
                setEditForm({ ...editForm, description: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FormUploadStructure;
